// File: components/PracticeRoom.tsx
import React, { useState, useRef, useEffect } from 'react';
import { PlayerProfile, GroupConfig, ViewState, CostumeId, ProducerSkills } from '../types';
import { UPGRADE_PRICES, COSTUME_DATA, MAX_SKILL } from '../constants';
import { Music, Mic, Bot, Sparkles, Heart, Users, Shirt, Check, Zap, Utensils } from 'lucide-react';
import { ExitButton } from './ExitButton';
import { drawRumi } from './renderer/player/drawRumi';
import { drawMira } from './renderer/allies/drawMira';
import { drawZoey } from './renderer/allies/drawZoey';
import { COLORS } from './config/colors';

interface PracticeRoomProps {
    player: PlayerProfile;
    onUpdatePlayer: (updates: Partial<PlayerProfile>) => void;
    onNavigate: (view: ViewState) => void;
    onExit: () => void;
    onNotify: (message: string, type?: 'info' | 'error' | 'success') => void;
}

// --- COSTUME PREVIEW COMPONENT ---
const CostumePreview: React.FC<{ costumeId: CostumeId }> = ({ costumeId }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;

        // Render Loop for simple idle animation
        let animId: number;
        const render = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();
            ctx.scale(dpr, dpr);

            // Positioning for 3 characters - MAXIMIZED LAYOUT
            const scale = 0.9; // Significantly larger to fill the box
            const baseY = rect.height - 10;
            const centerX = rect.width / 2;
            const offset = 75; // Increased spacing for larger sprites

            // --- Draw Zoey (Left - Back) ---
            ctx.save();
            ctx.translate(centerX - offset, baseY);
            ctx.scale(scale, scale);
            const dummyZoey = {
                x: -40, y: -200, width: 80, height: 200,
                color: '#fff', attackCooldown: 0, targetX: 0, isAttacking: false, attackTimer: 0
            };
            drawZoey(ctx, dummyZoey, costumeId);
            ctx.restore();

            // --- Draw Mira (Right - Back) ---
            ctx.save();
            ctx.translate(centerX + offset, baseY);
            ctx.scale(scale, scale);
            const dummyMira = {
                x: -40, y: -200, width: 80, height: 200,
                color: '#fff', attackCooldown: 0, targetX: 0, isAttacking: false, attackTimer: 0
            };
            drawMira(ctx, dummyMira, costumeId);
            ctx.restore();

            // --- Draw Rumi (Center - Front) ---
            ctx.save();
            ctx.translate(centerX, baseY + 10); // Slightly lower/forward
            ctx.scale(scale * 1.05, scale * 1.05); // Slightly larger perspective
            const dummyRumi = {
                x: -42, y: -215, width: 85, height: 215,
                vx: 0, vy: 0, isSwinging: false, swingTimer: 0, facing: 1 as const, color: COLORS.player
            };
            drawRumi(ctx, dummyRumi, false, costumeId);
            ctx.restore();

            ctx.restore();
            animId = requestAnimationFrame(render);
        };
        render();

        return () => cancelAnimationFrame(animId);
    }, [costumeId]);

    return (
        <canvas
            ref={canvasRef}
            className="w-full md:w-64 h-52 bg-slate-900/50 rounded-md border border-slate-700 shadow-inner"
        />
    );
};

// --- RENDERER FOR CARB LOADING SCENE (HIGH FIDELITY) ---
const drawEatingMember = (
    ctx: CanvasRenderingContext2D,
    memberId: string,
    x: number,
    y: number,
    time: number,
    isEating: boolean
) => {
    ctx.save();
    ctx.translate(x, y);

    // Scale for the practice room view (smaller than Spicy Challenge)
    ctx.scale(0.8, 0.8);

    // Bobbing animation
    const bob = Math.sin(time / 150 + (x * 0.1)) * 3;
    ctx.translate(0, bob);

    // 1. CONFIGURATION BASED ON MEMBER
    let hairColor = "#333";
    let hairHighlight = "#555";
    let outfitColor = "#333";
    let outfitDetail = "#555";
    let skinTone = "#ffdbac";
    let cupColor = "#fff";
    let cupTextColor = "#000";

    if (memberId === 'RUMI') {
        hairColor = "#8e44ad"; // Purple
        hairHighlight = "#a569bd";
        outfitColor = "#d4ac0d"; // Yellow Jacket
        outfitDetail = "#b9770e"; // Darker Yellow/Gold shadow
        cupColor = "#2e1065"; // Dark Purple
        cupTextColor = "#facc15"; // Yellow Text
    } else if (memberId === 'MIRA') {
        hairColor = "#db2777"; // Pink
        hairHighlight = "#f472b6";
        outfitColor = "#111827"; // Black Top
        outfitDetail = "#374151"; // Grey detail
        cupColor = "#be185d"; // Pink/Red
        cupTextColor = "#fff";
    } else if (memberId === 'ZOEY') {
        hairColor = "#0f172a"; // Dark Blue/Black
        hairHighlight = "#334155";
        outfitColor = "#0891b2"; // Cyan/Teal
        outfitDetail = "#164e63";
        cupColor = "#0e7490"; // Cyan
        cupTextColor = "#ecfeff";
    }

    // --- HAIR BACK (Braid for Rumi, Pigtails/Flowing for others) ---
    ctx.fillStyle = hairColor;
    if (memberId === 'RUMI') {
        const braidSegments = 14;
        for (let i = 0; i < braidSegments; i++) {
            const yPos = -95 + (i * 20);
            const w = 44 - (i * 1.5);
            const h = 32;
            const xSway = Math.sin(time / 400 + i * 0.4) * (i * 2.5);
            ctx.fillStyle = hairColor;
            ctx.beginPath();
            ctx.ellipse(xSway, yPos, w, h, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = hairHighlight;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(xSway - w / 2 + 8, yPos - 10);
            ctx.quadraticCurveTo(xSway, yPos - 20, xSway + w / 2 - 8, yPos - 10);
            ctx.stroke();
        }
        ctx.fillStyle = "#ffd700";
        ctx.beginPath(); ctx.arc(0, -95, 12, 0, Math.PI * 2); ctx.fill();

        // Sideburns - strands of hair framing the face
        ctx.fillStyle = hairColor;
        // Left sideburn
        ctx.beginPath();
        ctx.moveTo(-45, -60);
        ctx.quadraticCurveTo(-55, -30, -50, 10);
        ctx.quadraticCurveTo(-48, 20, -42, 15);
        ctx.quadraticCurveTo(-45, -20, -40, -55);
        ctx.closePath();
        ctx.fill();
        // Right sideburn
        ctx.beginPath();
        ctx.moveTo(45, -60);
        ctx.quadraticCurveTo(55, -30, 50, 10);
        ctx.quadraticCurveTo(48, 20, 42, 15);
        ctx.quadraticCurveTo(45, -20, 40, -55);
        ctx.closePath();
        ctx.fill();
    } else if (memberId === 'MIRA') {
        // Mira's UNIFIED long flowing hair - narrower at crown, knee-length
        const hairSway = Math.sin(time / 400) * 3;
        ctx.beginPath();
        // Start at top center of head (narrower)
        ctx.moveTo(0, -88);
        // Crown hugs head more tightly
        ctx.quadraticCurveTo(40, -85, 50, -50);
        // Transition to flowing hair
        ctx.quadraticCurveTo(60, -20, 65 + hairSway, 30);
        // Right side flows down to knees (shortened from 180 to 140)
        ctx.bezierCurveTo(68 + hairSway, 70, 58 + hairSway, 110, 45 + hairSway, 140);
        // Bottom right tip
        ctx.quadraticCurveTo(30 + hairSway, 155, 12, 145 + hairSway);
        // Inner curve at bottom
        ctx.quadraticCurveTo(0, 135 + hairSway, -12, 145 + hairSway);
        // Bottom left tip
        ctx.quadraticCurveTo(-30 + hairSway, 155, -45 + hairSway, 140);
        // Left side flows up
        ctx.bezierCurveTo(-58 + hairSway, 110, -68 + hairSway, 70, -65 + hairSway, 30);
        // Transition back to crown
        ctx.quadraticCurveTo(-60, -20, -50, -50);
        // Crown curves back to top (narrower)
        ctx.quadraticCurveTo(-40, -85, 0, -88);
        ctx.closePath();
        ctx.fill();
    }

    // --- BODY & OUTFIT ---
    ctx.fillStyle = outfitColor;

    // Shoulders - Broad and rounded
    ctx.beginPath();
    ctx.ellipse(0, 90, 60, 45, 0, Math.PI, 0);
    ctx.fill();

    // Outfit Details
    if (memberId === 'RUMI') {
        ctx.fillStyle = "#111"; // Inner shirt
        ctx.fillRect(-20, 50, 40, 40);
        ctx.fillStyle = outfitDetail;
        ctx.beginPath(); ctx.moveTo(-50, 55); ctx.lineTo(-20, 90); ctx.lineTo(-60, 90); ctx.fill();
        ctx.beginPath(); ctx.moveTo(50, 55); ctx.lineTo(20, 90); ctx.lineTo(60, 90); ctx.fill();
        ctx.fillStyle = outfitColor;
        ctx.beginPath(); ctx.moveTo(-35, 50); ctx.lineTo(-15, 80); ctx.lineTo(-50, 80); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(35, 50); ctx.lineTo(15, 80); ctx.lineTo(50, 80); ctx.closePath(); ctx.fill();
        ctx.fillStyle = "#ef4444"; ctx.fillRect(-55, 65, 10, 10);
        ctx.fillStyle = "#3b82f6"; ctx.beginPath(); ctx.arc(55, 70, 6, 0, Math.PI * 2); ctx.fill();
    } else if (memberId === 'MIRA') {
        ctx.fillStyle = "#fff"; ctx.font = "bold 6px Arial"; ctx.textAlign = "center";
        ctx.globalAlpha = 0.6; ctx.fillText("NO", 0, 75); ctx.fillText("LIMIT", 0, 82); ctx.globalAlpha = 1.0;
        ctx.fillStyle = "#000"; ctx.fillRect(-12, 45, 24, 4);
        ctx.fillStyle = "#facc15"; ctx.beginPath(); ctx.arc(0, 47, 2, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#333"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(-30, 50); ctx.lineTo(-30, 90); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(30, 50); ctx.lineTo(30, 90); ctx.stroke();
    } else if (memberId === 'ZOEY') {
        ctx.fillStyle = "#111"; ctx.fillRect(-35, 50, 6, 40); ctx.fillRect(29, 50, 6, 40);
        ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 1;
        ctx.beginPath(); for (let i = 0; i < 5; i++) { ctx.moveTo(-20 + i * 10, 60); ctx.lineTo(-20 + i * 10, 90); } ctx.stroke();
    }

    // --- NECK ---
    ctx.fillStyle = skinTone;
    ctx.fillRect(-12, 35, 24, 20);
    ctx.fillStyle = "rgba(0,0,0,0.1)";
    ctx.fillRect(-12, 35, 24, 5);

    // --- HAIR BACK MASS (Zoey only, Rumi/Mira handled above) ---
    if (memberId === 'ZOEY') {
        ctx.fillStyle = hairColor;
        ctx.beginPath(); ctx.arc(0, -25, 65, Math.PI, 0); ctx.fill();
        ctx.fillRect(-65, -25, 130, 65);
    }

    // --- FACE ---
    ctx.fillStyle = skinTone;
    ctx.beginPath();
    ctx.moveTo(-45, -40);
    ctx.quadraticCurveTo(-65, 30, -25, 60);
    ctx.quadraticCurveTo(0, 65, 25, 60);
    ctx.quadraticCurveTo(65, 30, 45, -40);
    ctx.quadraticCurveTo(45, -80, 0, -85);
    ctx.quadraticCurveTo(-45, -80, -45, -40);
    ctx.fill();

    ctx.fillStyle = skinTone;
    ctx.beginPath(); ctx.ellipse(-52, -5, 8, 14, -0.1, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(52, -5, 8, 14, 0.1, 0, Math.PI * 2); ctx.fill();

    const drawEye = (ex: number) => {
        ctx.save();
        ctx.translate(ex, -10);
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        const eyeSize = 16;
        ctx.ellipse(0, 0, eyeSize, eyeSize * 0.9, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 1; ctx.strokeStyle = "#000"; ctx.stroke();
        ctx.fillStyle = (memberId === 'RUMI' ? "#5b21b6" : (memberId === 'MIRA' ? "#be185d" : "#0e7490"));
        const irisSize = 6;
        ctx.beginPath(); ctx.arc(0, 0, irisSize, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#000"; ctx.beginPath(); ctx.arc(0, 0, irisSize * 0.4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(3, -3, 2, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#000"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(-eyeSize, -5); ctx.quadraticCurveTo(0, -eyeSize - 5, eyeSize, -5); ctx.stroke();
        ctx.lineWidth = 3; ctx.strokeStyle = hairColor;
        const browOffset = -eyeSize - 15;
        ctx.beginPath();
        if (memberId === 'ZOEY') { ctx.moveTo(-10, browOffset); ctx.lineTo(15, browOffset - 5); }
        else { ctx.moveTo(-15, browOffset); ctx.lineTo(15, browOffset + 5); }
        ctx.stroke();
        ctx.restore();
    };
    drawEye(-22); drawEye(22);

    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.beginPath(); ctx.arc(0, 15, 2, 0, Math.PI * 2); ctx.fill();

    // MOUTH (Base lips)
    ctx.save();
    ctx.translate(0, 35);
    ctx.fillStyle = "#fda4af";
    ctx.beginPath(); ctx.ellipse(0, 0, 12, 8, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#450a0a";
    ctx.beginPath(); ctx.ellipse(0, 0, 6, 4, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    // FRONT HAIR
    ctx.save();
    ctx.fillStyle = hairColor;
    ctx.beginPath();
    if (memberId === 'ZOEY') {
        ctx.moveTo(-65, -60); ctx.lineTo(-45, -35); ctx.lineTo(-20, -32); ctx.lineTo(0, -30);
        ctx.lineTo(20, -32); ctx.lineTo(45, -35); ctx.lineTo(65, -60);
        ctx.quadraticCurveTo(60, -90, 0, -95); ctx.quadraticCurveTo(-60, -90, -65, -60);
    } else if (memberId === 'MIRA') {
        ctx.moveTo(-60, -50); ctx.quadraticCurveTo(-20, -40, -5, -35); ctx.lineTo(0, -45); ctx.lineTo(5, -35);
        ctx.quadraticCurveTo(20, -40, 60, -50);
        ctx.quadraticCurveTo(60, -90, 0, -95); ctx.quadraticCurveTo(-60, -90, -60, -50);
    } else {
        ctx.moveTo(-60, -55); ctx.quadraticCurveTo(0, -35, 60, -55); ctx.quadraticCurveTo(60, -95, 0, -100);
        ctx.quadraticCurveTo(-60, -95, -60, -55);
    }
    ctx.fill();
    ctx.restore();

    // --- CUP BACK (Rim behind noodles) ---
    ctx.save();
    ctx.translate(0, 90);
    ctx.fillStyle = "#e5e5e5";
    ctx.beginPath(); ctx.ellipse(0, -30, 40, 8, 0, 0, Math.PI * 2); ctx.fill();
    // Front Rim Edge (Thin gray line representing top of lid)
    ctx.strokeStyle = "#d4d4d4"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(0, -30, 40, 8, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();

    // --- NOODLES (Drawn INSIDE Cup) ---
    if (isEating) {
        ctx.save();
        ctx.translate(0, 35);
        ctx.strokeStyle = "#fef9c3"; ctx.lineWidth = 4; ctx.lineCap = "round";
        for (let i = 0; i < 6; i++) {
            const offset = (i - 2.5) * 6;
            const wiggle = Math.sin(time / 80 + i) * 3;
            ctx.beginPath();
            ctx.moveTo(offset, 0);
            ctx.quadraticCurveTo(offset + wiggle, 25, 0, 65);
            ctx.stroke();
        }
        ctx.restore();
    }

    // --- CUP FRONT ---
    ctx.save();
    ctx.translate(0, 90);
    ctx.fillStyle = skinTone;
    ctx.beginPath(); ctx.ellipse(-45, 0, 18, 22, -0.3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(45, 0, 18, 22, 0.3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = cupColor;
    ctx.beginPath(); ctx.moveTo(-40, -30); ctx.lineTo(40, -30); ctx.lineTo(30, 60); ctx.lineTo(-30, 60); ctx.closePath(); ctx.fill();
    ctx.save();
    ctx.clip(new Path2D("M -40 -30 L 40 -30 L 30 60 L -30 60 Z"));
    ctx.fillStyle = "rgba(255,255,255,0.1)"; ctx.fillRect(-40, -10, 80, 40);
    ctx.fillStyle = cupTextColor; ctx.font = "900 24px 'Impact', sans-serif";
    ctx.textAlign = "center"; ctx.fillText(memberId, 0, 15);
    ctx.restore();
    ctx.save(); ctx.translate(50, -20); ctx.rotate(-0.3); ctx.fillStyle = "#8d6e63"; ctx.fillRect(0, 0, 4, 120);
    ctx.rotate(0.15); ctx.fillRect(10, 0, 4, 120); ctx.restore();
    ctx.restore();

    // PARTICLES (Steam)
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)"; ctx.lineWidth = 4; ctx.lineCap = "round";
    const steamY = (time / 15) % 30;
    ctx.beginPath(); ctx.moveTo(-15, -40); ctx.quadraticCurveTo(-10, -70 - steamY, -20, -100 - steamY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(15, -40); ctx.quadraticCurveTo(20, -70 - steamY, 10, -100 - steamY); ctx.stroke();

    ctx.restore();
};

export const PracticeRoom: React.FC<PracticeRoomProps> = ({ player, onUpdatePlayer, onNavigate, onExit, onNotify }) => {
    const { group, cash, skills } = player;
    const [tab, setTab] = useState<'PRODUCER SKILLS' | 'TRAINING' | 'RECOVERY' | 'WARDROBE'>('PRODUCER SKILLS');
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const startRehearsal = () => {
        onNavigate(ViewState.STAGE);
    };

    const getMasterUpgradeCost = () => {
        let totalCost = 0;
        const skillCost = UPGRADE_PRICES.VOCAL_LESSON;
        const spiritCostPerPoint = UPGRADE_PRICES.RESTORE_SPIRIT;

        player.group.members.forEach(member => {
            totalCost += Math.max(0, MAX_SKILL - member.vocal) * skillCost;
            totalCost += Math.max(0, MAX_SKILL - member.dance) * skillCost;
            totalCost += Math.max(0, MAX_SKILL - member.style) * skillCost;
            totalCost += Math.max(0, member.maxSpirit - member.spirit) * spiritCostPerPoint;
        });

        for (const skill of ['songwriting', 'choreography', 'marketing'] as const) {
            totalCost += Math.max(0, MAX_SKILL - player.skills[skill]) * skillCost;
        }

        (Object.keys(COSTUME_DATA) as CostumeId[]).forEach(costumeId => {
            if (!player.unlockedCostumes.includes(costumeId)) {
                totalCost += COSTUME_DATA[costumeId].price;
            }
        });

        return totalCost;
    };

    const masterUpgradeCost = getMasterUpgradeCost();

    const handleMaxEverything = () => {
        let totalCost = 0;
        const skillCost = UPGRADE_PRICES.VOCAL_LESSON;
        const spiritCostPerPoint = UPGRADE_PRICES.RESTORE_SPIRIT;

        const updatedMembers = player.group.members.map(member => {
            const vocalLevelsNeeded = Math.max(0, MAX_SKILL - member.vocal);
            const danceLevelsNeeded = Math.max(0, MAX_SKILL - member.dance);
            const styleLevelsNeeded = Math.max(0, MAX_SKILL - member.style);
            totalCost += (vocalLevelsNeeded + danceLevelsNeeded + styleLevelsNeeded) * skillCost;
            const spiritNeeded = Math.max(0, member.maxSpirit - member.spirit);
            totalCost += spiritNeeded * spiritCostPerPoint;
            return { ...member, vocal: MAX_SKILL, dance: MAX_SKILL, style: MAX_SKILL, spirit: member.maxSpirit };
        });

        const updatedSkills = { ...player.skills };
        for (const skill of ['songwriting', 'choreography', 'marketing'] as const) {
            const levelsNeeded = Math.max(0, MAX_SKILL - player.skills[skill]);
            totalCost += levelsNeeded * skillCost;
            updatedSkills[skill] = MAX_SKILL;
        }

        const updatedUnlockedCostumes = [...player.unlockedCostumes];
        (Object.keys(COSTUME_DATA) as CostumeId[]).forEach(costumeId => {
            if (!player.unlockedCostumes.includes(costumeId)) {
                totalCost += COSTUME_DATA[costumeId].price;
                updatedUnlockedCostumes.push(costumeId);
            }
        });

        if (player.cash >= totalCost) {
            onUpdatePlayer({
                cash: player.cash - totalCost,
                group: { ...player.group, members: updatedMembers },
                skills: updatedSkills,
                unlockedCostumes: updatedUnlockedCostumes,
            });
            onNotify(`BUY EVERYTHING Complete!`, 'success');
        } else {
            onNotify(`Need $${totalCost.toLocaleString()}`, 'error');
        }
    };

    const trainSkill = (memberIndex: number, skill: 'vocal' | 'dance' | 'style') => {
        const member = group.members[memberIndex];
        const cost = UPGRADE_PRICES.VOCAL_LESSON;
        if (cash >= cost && member[skill] < MAX_SKILL) {
            const newMembers = [...group.members];
            newMembers[memberIndex] = { ...member, [skill]: member[skill] + 1 };
            onUpdatePlayer({ cash: cash - cost, group: { ...group, members: newMembers } });
        }
    };

    const trainProducerSkill = (skill: keyof ProducerSkills) => {
        const cost = UPGRADE_PRICES.VOCAL_LESSON;
        if (cash >= cost && player.skills[skill] < MAX_SKILL) {
            onUpdatePlayer({
                cash: cash - cost,
                skills: { ...player.skills, [skill]: player.skills[skill] + 1 },
            });
        }
    };

    const recoverSpirit = (memberIndex: number) => {
        const member = group.members[memberIndex];
        const spiritToHeal = Math.min(10, member.maxSpirit - member.spirit);
        const cost = UPGRADE_PRICES.RESTORE_SPIRIT * spiritToHeal;
        if (cash >= cost && member.spirit < member.maxSpirit) {
            const newMembers = [...group.members];
            newMembers[memberIndex] = { ...member, spirit: member.spirit + spiritToHeal };
            onUpdatePlayer({ cash: cash - cost, group: { ...group, members: newMembers } });
        }
    };

    const recoverAll = () => {
        let totalCost = 0;
        const updatedMembers = group.members.map(member => {
            const spiritToHeal = member.maxSpirit - member.spirit;
            const cost = spiritToHeal * UPGRADE_PRICES.RESTORE_SPIRIT;
            if (spiritToHeal > 0) totalCost += cost;
            return { ...member, spirit: member.maxSpirit };
        });

        if (totalCost === 0) {
            onNotify("Everyone is already full!", 'info');
            return;
        }

        if (cash >= totalCost) {
            onUpdatePlayer({ cash: cash - totalCost, group: { ...group, members: updatedMembers } });
            onNotify(`Full Carb Load Complete! (-$${totalCost})`, 'success');
        } else {
            onNotify(`Need $${totalCost.toLocaleString()} to feed everyone.`, 'error');
        }
    };

    const equipCostume = (costumeId: CostumeId) => {
        const newMembers = group.members.map(m => ({ ...m, costume: costumeId }));
        onUpdatePlayer({ group: { ...group, members: newMembers } });
    };

    useEffect(() => {
        if (tab !== 'RECOVERY') return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let animId: number;
        const animate = () => {
            const time = Date.now();
            const rect = canvas.getBoundingClientRect();
            if (canvas.width !== rect.width || canvas.height !== rect.height) {
                canvas.width = rect.width; canvas.height = rect.height;
            }
            const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
            grad.addColorStop(0, "#4a1d4d"); grad.addColorStop(1, "#831843");
            ctx.fillStyle = grad; ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = "#cbd5e1"; ctx.fillRect(0, canvas.height - 40, canvas.width, 40);
            ctx.fillStyle = "#94a3b8"; ctx.fillRect(0, canvas.height - 40, canvas.width, 10);
            const centerX = canvas.width / 2; const centerY = canvas.height - 80; const spacing = 120;
            drawEatingMember(ctx, 'MIRA', centerX - spacing, centerY, time, true);
            drawEatingMember(ctx, 'RUMI', centerX, centerY, time, true);
            drawEatingMember(ctx, 'ZOEY', centerX + spacing, centerY, time, true);
            animId = requestAnimationFrame(animate);
        };
        animate(); return () => cancelAnimationFrame(animId);
    }, [tab]);

    return (
        <div className="h-full w-full bg-slate-950 p-8 flex flex-col lg:flex-row gap-8 overflow-y-auto relative">
            <ExitButton onExit={onExit} />
            <div className="flex-1 glass-panel p-6 md:p-8 flex flex-col min-h-[400px]">
                <h2 className="text-xl md:text-2xl font-heading text-slate-200 mb-6">HUNTR/X Members</h2>
                <div className="flex-1 w-full space-y-4 text-base overflow-y-auto pr-2 relative custom-scrollbar">
                    <div className="pb-12">
                        {group.members.map((member, index) => (
                            <div key={index} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 relative overflow-hidden mb-4 last:mb-0">
                                <div className="relative z-10">
                                    <div className="font-bold text-lg text-pink-300">{member.name} <span className="text-sm text-slate-400 capitalize">({member.role.toLowerCase()})</span></div>
                                    <div className="text-sm text-slate-400">Spirit: {member.spirit}/{member.maxSpirit}</div>
                                    <div className="text-xs text-slate-500 mt-2 flex gap-4">
                                        <span>Vocal: {member.vocal - (member.vocalDebuff || 0)} {member.vocalDebuff ? <span className="text-red-500">(-{member.vocalDebuff})</span> : ''}</span>
                                        <span>Dance: {member.dance}</span>
                                        <span>Style: {member.style}</span>
                                    </div>
                                </div>
                                <div className="absolute top-4 right-4 bg-slate-800 px-2 py-1 rounded text-[10px] font-bold text-slate-400 uppercase border border-slate-700">
                                    {COSTUME_DATA[member.costume || 'DEFAULT'].label}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Scroll Hint Fade */}
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none z-20"></div>
                </div>
                <button onClick={startRehearsal} className="mt-8 w-full py-4 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg uppercase tracking-widest border-b-4 border-cyan-800 shadow-[0_0_15px_#0891b2] transition-transform active:scale-95">
                    START REHEARSAL
                </button>
            </div>
            <div className="flex-1 space-y-8">
                <header className="flex justify-between items-end border-b-2 border-slate-800 pb-4">
                    <h2 className="text-4xl font-heading text-slate-200">Practice Room</h2>
                </header>
                <section className="bg-gradient-to-r from-purple-800 to-pink-800 p-6 rounded-xl shadow-[0_0_30px_rgba(217,70,239,0.3)] text-center border-2 border-purple-500/50">
                    <button onClick={handleMaxEverything} className="w-full py-4 bg-white hover:bg-slate-200 text-black rounded-lg transition-transform hover:scale-105 active:scale-100 shadow-2xl flex flex-col items-center justify-center gap-1">
                        <span className="text-xs font-black text-purple-600 tracking-[0.2em] uppercase">BUY EVERYTHING</span>
                        <div className="flex items-center gap-2 font-heading text-2xl"><Zap size={24} className="text-purple-600" /><span>$ {masterUpgradeCost.toLocaleString()}</span></div>
                    </button>
                </section>
                <div className="flex gap-2 p-1 bg-slate-800/50 rounded-md border border-slate-700">
                    {(['PRODUCER SKILLS', 'TRAINING', 'RECOVERY', 'WARDROBE'] as const).map(t => <button key={t} onClick={() => setTab(t)} className={`flex-1 py-2 rounded font-bold text-sm ${tab === t ? 'bg-pink-500 text-white' : 'text-slate-300 hover:bg-slate-700'}`}>{t}</button>)}
                </div>
                {tab === 'TRAINING' && (
                    <section className="glass-panel p-6 rounded-lg space-y-2">
                        <h3 className="text-xl font-heading text-slate-200 mb-4 flex items-center gap-2"><Sparkles /> Member Training</h3>
                        {group.members.map((m, i) => (
                            <div key={i} className="bg-slate-800/50 p-3 rounded-md">
                                <div className="font-bold text-slate-300 mb-2">{m.name}</div>
                                <div className="flex gap-2">
                                    <button onClick={() => trainSkill(i, 'vocal')} className="flex-1 text-xs text-center p-2 bg-slate-700 hover:bg-pink-500/50 rounded-md border border-slate-600">Vocal+</button>
                                    <button onClick={() => trainSkill(i, 'dance')} className="flex-1 text-xs text-center p-2 bg-slate-700 hover:bg-pink-500/50 rounded-md border border-slate-600">Dance+</button>
                                    <button onClick={() => trainSkill(i, 'style')} className="flex-1 text-xs text-center p-2 bg-slate-700 hover:bg-pink-500/50 rounded-md border border-slate-600">Style+</button>
                                </div>
                            </div>
                        ))}
                    </section>
                )}
                {tab === 'RECOVERY' && (
                    <section className="glass-panel p-4 rounded-lg space-y-4 text-base relative">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-xl font-heading text-slate-200 flex items-center gap-2"><Heart /> Recovery Zone</h3>
                            <button onClick={recoverAll} className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white font-bold rounded-lg text-xs flex items-center gap-2 shadow-lg"><Utensils size={14} /> FEED EVERYONE</button>
                        </div>
                        <div className="relative w-full h-48 bg-slate-900 rounded-lg overflow-hidden border-2 border-slate-700 shadow-inner">
                            <canvas ref={canvasRef} className="w-full h-full object-cover" />
                            <div className="absolute top-2 left-2 bg-black/50 px-2 py-1 rounded text-[10px] text-white font-bold">LIVE FEED: EATING</div>
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                            {group.members.map((member, i) => (
                                <div key={i} className="flex justify-between items-center bg-slate-800/50 p-2 rounded-md">
                                    <span className="text-slate-300 text-sm">{member.name} ({member.spirit}/{member.maxSpirit})</span>
                                    <button onClick={() => recoverSpirit(i)} disabled={member.spirit >= member.maxSpirit} className="text-xs font-bold bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-2 rounded-md border border-slate-600">
                                        {member.spirit >= member.maxSpirit ? 'FULL' : 'CARB LOAD (+$1k)'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                {tab === 'WARDROBE' && (
                    <section className="glass-panel p-6 rounded-lg space-y-4 text-base">
                        <h3 className="text-xl font-heading text-slate-200 mb-4 flex items-center gap-2"><Shirt /> Fitting Room</h3>
                        <div className="space-y-3">
                            {(Object.keys(COSTUME_DATA) as CostumeId[]).map(id => {
                                const data = COSTUME_DATA[id];
                                const isUnlocked = player.unlockedCostumes.includes(id);
                                const isEquipped = group.members.every(m => m.costume === id);
                                return (
                                    <div key={id} className={`p-4 rounded-lg border flex flex-col md:flex-row gap-4 items-center ${isEquipped ? 'bg-pink-900/20 border-pink-500' : 'bg-slate-800/50 border-slate-700'}`}>
                                        <CostumePreview costumeId={id} />
                                        <div className="flex-1 w-full">
                                            <div className="font-bold text-slate-200 flex items-center gap-2">{data.label}{isEquipped && <Check size={16} className="text-green-400" />}</div>
                                            <div className="text-xs text-slate-400">{data.description}</div>
                                        </div>
                                        <div className="w-full md:w-auto">
                                            {isUnlocked ? (<button onClick={() => equipCostume(id)} disabled={isEquipped} className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:bg-slate-800 text-white text-xs font-bold rounded">{isEquipped ? 'EQUIPPED' : 'EQUIP ALL'}</button>) : (<div className="w-full px-4 py-2 bg-slate-800 text-slate-500 text-xs font-bold rounded border border-slate-700 text-center">LOCKED</div>)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}
                {tab === 'PRODUCER SKILLS' && (
                    <section className="glass-panel p-6 rounded-lg space-y-2 text-base">
                        <h3 className="text-xl font-heading text-slate-200 mb-4 flex items-center gap-2"><Users /> Producer Skills</h3>
                        <div className="space-y-3">
                            <div className="bg-slate-800/50 p-3 rounded-md flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-slate-700 rounded-md"><Music size={16} className="text-pink-400" /></div>
                                    <div><div className="text-slate-400 font-bold text-sm">Songwriting</div><div className="font-bold text-slate-200 text-lg">{skills.songwriting}</div></div>
                                </div>
                                <button onClick={() => trainProducerSkill('songwriting')} className="text-xs font-bold bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-md border border-slate-600">TRAIN</button>
                            </div>
                            <div className="bg-slate-800/50 p-3 rounded-md flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-slate-700 rounded-md"><Bot size={16} className="text-cyan-400" /></div>
                                    <div><div className="text-slate-400 font-bold text-sm">Choreography</div><div className="font-bold text-slate-200 text-lg">{skills.choreography}</div></div>
                                </div>
                                <button onClick={() => trainProducerSkill('choreography')} className="text-xs font-bold bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-md border border-slate-600">TRAIN</button>
                            </div>
                            <div className="bg-slate-800/50 p-3 rounded-md flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-slate-700 rounded-md"><Mic size={16} className="text-green-400" /></div>
                                    <div><div className="text-slate-400 font-bold text-sm">Marketing</div><div className="font-bold text-slate-200 text-lg">{skills.marketing}</div></div>
                                </div>
                                <button onClick={() => trainProducerSkill('marketing')} className="text-xs font-bold bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-md border border-slate-600">TRAIN</button>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};
